/**
 * Central helper to build a safe route to a recipe detail page.
 *
 * Handles:
 * - null / undefined / empty category → fallback "general"
 * - spaces and special chars → slug-friendly
 * - AI-generated recipes → /my-recipes/:id
 * - Includes the recipe UUID in the path so navigation stays stable
 *   even when titles/categories are translated in the UI
 */

import { nameToSlug } from "@/lib/collectionSlugs";

const slugifyCategory = (category: string | null | undefined): string => {
  if (!category || !category.trim()) return "general";
  return nameToSlug(category.trim());
};

export interface RecipeRoutable {
  id: string;
  title?: string;
  category?: string | null;
  /** If true, the recipe is AI-generated and uses /my-recipes/:id */
  isAiGenerated?: boolean;
}

/**
 * Build a safe URL for a recipe detail page.
 * Uses slugified title for clean URLs, falling back to UUID if no title.
 *
 * @example
 * buildRecipeRoute({ id: "123e4567-e89b-12d3-a456-426614174000", category: "breakfast", title: "Huevos Rancheros" })
 * // → "/recipes/breakfast/huevos-rancheros--123e4567-e89b-12d3-a456-426614174000"
 *
 * buildRecipeRoute({ id: "abc", isAiGenerated: true })
 * // → "/my-recipes/abc"
 */
export const buildRecipeRoute = (recipe: RecipeRoutable): string => {
  if (recipe.isAiGenerated) {
    return `/my-recipes/${recipe.id}`;
  }
  const catSlug = slugifyCategory(recipe.category);
  const titleSlug = recipe.title ? nameToSlug(recipe.title) : "receta";
  const recipeSlug = `${titleSlug}--${recipe.id}`;
  return `/recipes/${catSlug}/${recipeSlug}`;
};
